import "../../styles/index.css";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogContent,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ProgressBar from "../../components/progressbar/progressbar";
import CircularProgress from "@mui/material/CircularProgress";
import BASE_URL from "../../components/config/appconfig";
import axios from "axios";
import SnackbarAlert from "../../components/snackbarAlert/snackbarAlert";
import usePageMeta from "../../utils/usePageMeta";
import renderOptionText from "../../utils/renderOptionText";

const shuffleArray = (arr) => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const buildDisplayItems = (questions) => {
  const objectives = [];
  let reflectionItem = null;

  for (const q of questions) {
    if (q.objective) {
      objectives.push({
        type: "objective",
        questionId: q.objective._id,
        data: q.objective,
      });
    }
    if (q.reflection) {
      // Requirement: exactly one reflective question per subsection, always last
      reflectionItem = {
        type: "reflection",
        questionId: q.reflection._id,
        data: q.reflection,
      };
    }
  }

  return reflectionItem ? [...objectives, reflectionItem] : objectives;
};

const Assessment = () => {
  usePageMeta("Framework", "Complete your Gospel and Church Health Framework.");
  const [sectionName, setSectionName] = useState("");
  const [subSectionName, setSubSectionName] = useState("");
  const [sectionQuestions, setSectionQuestions] = useState([]);
  const [reflectionAnswer, setReflectionAnswer] = useState("");
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [subSections, setSubSections] = useState([]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentSubSectionIndex, setCurrentSubSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const location = useLocation();
  const assessmentId =
    location.state?.assessmentId ||
    sessionStorage.getItem("currentAssessmentId") ||
    null;
  const navigate = useNavigate();
  const [selfRating, setSelfRating] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [submitting, setSubmitting] = useState(false);
  const [totalQuestionsCount, setTotalQuestionsCount] = useState(0);
  const [completedQuestions, setCompletedQuestions] = useState(0);
  const [allResponses, setAllResponses] = useState([]);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [isAssessmentLocked, setIsAssessmentLocked] = useState(false);
  const [reflectionLocationMap, setReflectionLocationMap] = useState({});
  const [questionOffsetMap, setQuestionOffsetMap] = useState({});
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const cancellingRef = useRef(false);

  // Flat list of one-question-per-screen items derived from the current subsection's questions
  const displayItems = useMemo(
    () => buildDisplayItems(sectionQuestions),
    [sectionQuestions],
  );

  const currentDisplayItem = displayItems[currentQuestionIndex];

  const shuffledOptions = useMemo(() => {
    if (currentDisplayItem?.type !== "objective") return [];
    return shuffleArray(currentDisplayItem.data.question_options || []);
  }, [currentDisplayItem]);

  // Mandatory mode: reflection becomes required only after the user has fully
  // completed (text + rating) at least one reflection question.
  const hasAnyCompletedReflection = useMemo(
    () =>
      allResponses.some(
        (r) =>
          r.reflection &&
          r.reflection.reflection_text?.trim() !== "" &&
          r.reflection.self_rating > 0,
      ),
    [allResponses],
  );

  const currentAbsoluteIndex =
    (questionOffsetMap[currentSectionIndex]?.[currentSubSectionIndex] ?? 0) +
    currentQuestionIndex;
  const rawProgress =
    totalQuestionsCount > 0
      ? (currentAbsoluteIndex / totalQuestionsCount) * 100
      : 0;
  // Minimum visual progress for UI
  const progressPercent = Math.max(rawProgress, 5);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = async () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
    if (pendingNavigation) {
      const loc = pendingNavigation;
      setPendingNavigation(null);
      setReflectionAnswer("");
      setSelfRating(0);
      setSelectedOptionId(null);
      if (loc.sectionIndex !== currentSectionIndex) {
        setCurrentSectionIndex(loc.sectionIndex);
        setSectionName(loc.sectionName);
        await fetchSubSection(
          loc.sectionId,
          loc.subSectionIndex,
          loc.questionIndex,
        );
      } else if (loc.subSectionIndex !== currentSubSectionIndex) {
        setCurrentSubSectionIndex(loc.subSectionIndex);
        setSubSectionName(loc.subSectionName);
        await fetchQuestions(loc.subSectionId, loc.questionIndex);
      } else {
        setCurrentQuestionIndex(loc.questionIndex);
      }
    }
  };

  const fetchQuestions = useCallback(
    async (subSectionId, specificQuestionIndex = 0) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/questions/subsection/${subSectionId}`,
        );
        if (response.data.success) {
          const sortedQuestions = response.data.data.sort(
            (a, b) => a.display_order - b.display_order,
          );
          setSectionQuestions(sortedQuestions);
          const items = buildDisplayItems(sortedQuestions);
          const validIdx =
            specificQuestionIndex < items.length ? specificQuestionIndex : 0;
          setCurrentQuestionIndex(validIdx);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    },
    [],
  );

  const fetchSubSection = useCallback(
    async (sectionId, specificSubIndex = 0, specificQuestionIndex = 0) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/subsections/sections-id/${sectionId}`,
        );
        if (response.data.success) {
          const subSectionList = response.data.data;
          if (!subSectionList.length) return;
          setSubSections(subSectionList);
          const validIdx =
            specificSubIndex < subSectionList.length ? specificSubIndex : 0;
          setCurrentSubSectionIndex(validIdx);
          const subSection = subSectionList[validIdx];
          setSubSectionName(subSection.name || "");

          if (validIdx === specificSubIndex) {
            await fetchQuestions(subSection._id, specificQuestionIndex);
          } else {
            await fetchQuestions(subSection._id, 0);
          }
        }
      } catch (error) {
        console.error("Error fetching subsections:", error);
      }
    },
    [fetchQuestions],
  );

  const userData = localStorage.getItem("userData");
  const parsedUser = userData ? JSON.parse(userData) : null;
  const peopleId = parsedUser?._id;

  // Poll lock status every 30s so users mid-session get blocked if admin starts editing
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/settings/assessment-status`,
        );
        if (res.data?.success && res.data?.isLocked) {
          setIsAssessmentLocked(true);
        }
      } catch {
        // ignore poll errors
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSection = useCallback(async () => {
    if (cancellingRef.current) return;
    if (!assessmentId) {
      navigate("/verification", { replace: true });
      setLoading(false);
      return;
    }

    try {
      // Round 1 (parallel): validate assessment + lock status + sections list
      const [asmtRes, statusRes, sectionsRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/assessments/assessments-id/${assessmentId}`),
        axios.get(`${BASE_URL}/api/settings/assessment-status`),
        axios.get(`${BASE_URL}/api/sections`),
      ]);

      if (!asmtRes.data?.success || asmtRes.data?.data?.is_deleted) {
        navigate("/verification", { replace: true });
        setLoading(false);
        return;
      }

      if (statusRes.data?.success && statusRes.data?.isLocked) {
        setIsAssessmentLocked(true);
        setLoading(false);
        return;
      }

      const sectionList = sectionsRes.data.data;
      setSections(sectionList);

      // Round 2 (parallel): progress + responses + all subsections
      const [progRes, respRes, ...allSubResponses] = await Promise.all([
        axios.get(`${BASE_URL}/api/progress/${assessmentId}/${peopleId}`),
        axios.get(`${BASE_URL}/api/response/${assessmentId}`),
        ...sectionList.map((section) =>
          axios.get(`${BASE_URL}/api/subsections/sections-id/${section._id}`),
        ),
      ]);

      let savedSectionIndex = 0;
      let savedSubSectionIndex = 0;
      let savedQuestionIndex = 0;
      let completed = 0;

      if (progRes.data?.success && progRes.data?.data) {
        savedSectionIndex = progRes.data.data.current_section_index || 0;
        savedSubSectionIndex = progRes.data.data.current_subsection_index || 0;
        savedQuestionIndex = progRes.data.data.current_question_index || 0;
        completed = progRes.data.data.completed_questions || 0;
      }
      if (respRes.data?.success && respRes.data?.data) {
        setAllResponses(respRes.data.data);
      }

      const allSubLists = allSubResponses.map((r) => r.data.data);

      // Round 3 (parallel): all questions for every subsection
      const allSubsFlat = allSubLists.flatMap((subList, sIdx) =>
        subList.map((sub, subIdx) => ({ sub, sIdx, subIdx })),
      );
      const allQResponses = await Promise.all(
        allSubsFlat.map(({ sub }) =>
          axios.get(`${BASE_URL}/api/questions/subsection/${sub._id}`),
        ),
      );

      // Build maps (CPU only — no more I/O after this)
      let total = 0;
      const locationMap = {};
      const offsetMap = {};
      let flatIdx = 0;
      for (let sIdx = 0; sIdx < sectionList.length; sIdx++) {
        offsetMap[sIdx] = {};
        const section = sectionList[sIdx];
        const subList = allSubLists[sIdx];
        for (let subIdx = 0; subIdx < subList.length; subIdx++) {
          offsetMap[sIdx][subIdx] = total;
          const sub = subList[subIdx];
          const qData = allQResponses[flatIdx].data.data;
          flatIdx++;
          const items = buildDisplayItems(qData);
          total += items.length;
          for (const q of qData) {
            if (q.reflection) {
              locationMap[q.reflection._id] = {
                sectionOrder: section.display_order ?? sIdx + 1,
                sectionName: section.name,
                subSectionOrder: sub.display_order ?? subIdx + 1,
                subSectionName: sub.name,
                sectionIndex: sIdx,
                subSectionIndex: subIdx,
                sectionId: section._id,
                subSectionId: sub._id,
                questionIndex: items.length - 1,
              };
            }
          }
        }
      }
      setTotalQuestionsCount(total);
      setReflectionLocationMap(locationMap);
      setQuestionOffsetMap(offsetMap);

      // Set current position using already-fetched data — no extra requests
      const validSectionIndex =
        savedSectionIndex < sectionList.length ? savedSectionIndex : 0;
      setCurrentSectionIndex(validSectionIndex);
      setSectionName(sectionList[validSectionIndex].name || "");

      const subList = allSubLists[validSectionIndex];
      const validSubIdx =
        savedSubSectionIndex < subList.length ? savedSubSectionIndex : 0;
      setSubSections(subList);
      setCurrentSubSectionIndex(validSubIdx);
      setSubSectionName(subList[validSubIdx]?.name || "");

      const currentFlatIdx = allSubsFlat.findIndex(
        ({ sIdx, subIdx }) =>
          sIdx === validSectionIndex && subIdx === validSubIdx,
      );
      const currentQData = allQResponses[currentFlatIdx].data.data
        .slice()
        .sort((a, b) => a.display_order - b.display_order);
      setSectionQuestions(currentQData);
      const currentItems = buildDisplayItems(currentQData);
      const validQIdx =
        savedQuestionIndex < currentItems.length ? savedQuestionIndex : 0;
      setCurrentQuestionIndex(validQIdx);
      setCompletedQuestions(completed);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching sections:", error);
      setLoading(false);
    }
  }, [assessmentId, peopleId, navigate]);

  useEffect(() => {
    fetchSection();
  }, [fetchSection]);

  // Persist assessmentId so page refreshes don't lose it
  useEffect(() => {
    if (assessmentId) {
      sessionStorage.setItem("currentAssessmentId", assessmentId);
    }
  }, [assessmentId]);

  // Block browser back button while assessment is in progress
  useEffect(() => {
    window.history.pushState(null, "", window.location.pathname);
    const blockBack = () => {
      window.history.pushState(null, "", window.location.pathname);
    };
    window.addEventListener("popstate", blockBack);
    return () => window.removeEventListener("popstate", blockBack);
  }, []);

  const isLastQuestion =
    currentSectionIndex === sections.length - 1 &&
    currentSubSectionIndex === subSections.length - 1 &&
    currentQuestionIndex === displayItems.length - 1;

  // Load saved answers when navigating to a new question screen
  useEffect(() => {
    if (!currentDisplayItem) return;

    const questionId = currentDisplayItem.questionId;
    const saved = allResponses.find((r) => r.question?.id === questionId);

    if (saved) {
      if (currentDisplayItem.type === "objective") {
        setSelectedOptionId(saved.objective?.selected_option_id || null);
        setReflectionAnswer("");
        setSelfRating(0);
      } else {
        setSelectedOptionId(null);
        setReflectionAnswer(saved.reflection?.reflection_text || "");
        setSelfRating(saved.reflection?.self_rating || 0);
      }
    } else {
      setSelectedOptionId(null);
      setReflectionAnswer("");
      setSelfRating(0);
    }
  }, [
    currentQuestionIndex,
    currentSubSectionIndex,
    currentSectionIndex,
    currentDisplayItem,
    allResponses,
  ]);

  const saveProgressToDB = async (nextSec, nextSub, nextQues, completed) => {
    try {
      if (assessmentId) {
        const userId = parsedUser?.auth?.id;
        await axios.post(`${BASE_URL}/api/progress`, {
          assessment_id: assessmentId,
          people_id: peopleId,
          current_section_index: nextSec,
          current_subsection_index: nextSub,
          current_question_index: nextQues,
          completed_questions: completed,
          created_by: userId,
        });
      }
    } catch (err) {
      console.error("Failed to save progress", err);
    }
  };

  const saveResponseToDB = async () => {
    if (!currentDisplayItem) return allResponses;
    const questionId = currentDisplayItem.questionId;

    const responseData = {
      assessment_id: assessmentId,
      question: { id: questionId },
      objective:
        currentDisplayItem.type === "objective"
          ? { selected_option_id: selectedOptionId }
          : null,
      reflection:
        currentDisplayItem.type === "reflection"
          ? { reflection_text: reflectionAnswer, self_rating: selfRating }
          : null,
    };

    let updatedResponses = [...allResponses];
    const existingIndex = updatedResponses.findIndex(
      (r) => r.question?.id === questionId,
    );
    if (existingIndex !== -1) {
      updatedResponses[existingIndex] = responseData;
    } else {
      updatedResponses.push(responseData);
    }
    setAllResponses(updatedResponses);

    try {
      await axios.post(`${BASE_URL}/api/response`, {
        assessment_id: assessmentId,
        created_by: peopleId,
        ...(currentDisplayItem.type === "objective"
          ? {
              objective_response: {
                question_id: questionId,
                selected_option_id: selectedOptionId,
              },
            }
          : {
              reflection_response: {
                question_id: questionId,
                reflection_text: reflectionAnswer,
                self_rating: selfRating,
              },
            }),
      });
    } catch (err) {
      console.error("Failed to save response", err);
    }

    return updatedResponses;
  };

  // canProceed is type-aware: objective requires a selected option; reflection requires text + rating
  const canProceed = useMemo(() => {
    if (!currentDisplayItem) return false;
    if (currentDisplayItem.type === "objective") {
      return selectedOptionId !== null;
    }
    // If text is entered, rating becomes mandatory regardless of history.
    if (reflectionAnswer.trim() !== "") {
      return selfRating > 0;
    }
    // If a prior reflection was fully completed, this one is now mandatory too.
    if (hasAnyCompletedReflection) {
      return false;
    }
    // No prior completed reflection and no text entered — still optional, can skip.
    return true;
  }, [
    currentDisplayItem,
    selectedOptionId,
    reflectionAnswer,
    selfRating,
    hasAnyCompletedReflection,
  ]);

  const handleNext = async () => {
    setSubmitting(true);
    const updatedResponses = await saveResponseToDB();

    if (isLastQuestion) {
      const anyReflectionCompleted = updatedResponses.some(
        (r) =>
          r.reflection &&
          r.reflection.reflection_text?.trim() !== "" &&
          r.reflection.self_rating > 0,
      );

      if (anyReflectionCompleted) {
        const missingReflections = updatedResponses.filter(
          (r) =>
            r.reflection &&
            (r.reflection.reflection_text?.trim() === "" ||
              !r.reflection.self_rating),
        );

        if (missingReflections.length > 0) {
          const loc = reflectionLocationMap[missingReflections[0].question.id];
          setSubmitting(false);

          if (loc) {
            setPendingNavigation(loc);
            showSnackbar(
              `Incomplete reflection in Section ${loc.sectionOrder}.${loc.subSectionOrder}: ${loc.subSectionName}. Taking you there now.`,
              "warning",
            );
          } else {
            showSnackbar(
              "Please complete all reflection questions before submitting.",
              "error",
            );
          }
          return;
        }
      }

      await saveProgressToDB(
        currentSectionIndex,
        currentSubSectionIndex,
        currentQuestionIndex,
        completedQuestions + 1,
      );

      await axios.post(`${BASE_URL}/api/progress/complete`, {
        assessment_id: assessmentId,
      });

      showSnackbar("Framework submitted successfully!", "success");

      setTimeout(() => {
        navigate(`/feedback/${assessmentId}`, { replace: true });
      }, 2000);
      return;
    }

    const newCompleted = completedQuestions + 1;
    setCompletedQuestions(newCompleted);

    let nextSec = currentSectionIndex;
    let nextSub = currentSubSectionIndex;
    let nextQues = currentQuestionIndex;

    if (currentQuestionIndex < displayItems.length - 1) {
      nextQues = currentQuestionIndex + 1;
    } else if (currentSubSectionIndex < subSections.length - 1) {
      nextSub = currentSubSectionIndex + 1;
      nextQues = 0;
    } else if (currentSectionIndex < sections.length - 1) {
      nextSec = currentSectionIndex + 1;
      nextSub = 0;
      nextQues = 0;
    }

    await saveProgressToDB(nextSec, nextSub, nextQues, newCompleted);

    setReflectionAnswer("");
    setSelfRating(0);
    setSelectedOptionId(null);

    if (nextSec !== currentSectionIndex) {
      // fetchSubSection sets currentSubSectionIndex and fetchQuestions sets
      // currentQuestionIndex — don't set them early to avoid flash of stale content
      setCurrentSectionIndex(nextSec);
      const nextSection = sections[nextSec];
      setSectionName(nextSection.name);
      await fetchSubSection(nextSection._id, nextSub, nextQues);
    } else if (nextSub !== currentSubSectionIndex) {
      // fetchQuestions sets currentQuestionIndex — defer to avoid flash
      setCurrentSectionIndex(nextSec);
      setCurrentSubSectionIndex(nextSub);
      const nextSubSection = subSections[nextSub];
      setSubSectionName(nextSubSection.name);
      await fetchQuestions(nextSubSection._id, nextQues);
    } else {
      // Same subsection: no fetch needed, set question index directly
      setCurrentSectionIndex(nextSec);
      setCurrentSubSectionIndex(nextSub);
      setCurrentQuestionIndex(nextQues);
    }

    setSubmitting(false);
  };

  const handlePrevious = async () => {
    let didMove = false;

    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      didMove = true;
    } else if (currentSubSectionIndex > 0) {
      const prevSubIndex = currentSubSectionIndex - 1;
      const prevSubSection = subSections[prevSubIndex];

      setCurrentSubSectionIndex(prevSubIndex);
      setSubSectionName(prevSubSection.name);

      const response = await axios.get(
        `${BASE_URL}/api/questions/subsection/${prevSubSection._id}`,
      );

      if (response.data.success) {
        const sortedQuestions = response.data.data.sort(
          (a, b) => a.display_order - b.display_order,
        );
        setSectionQuestions(sortedQuestions);
        const prevItems = buildDisplayItems(sortedQuestions);
        setCurrentQuestionIndex(prevItems.length - 1);
        didMove = true;
      }
    } else if (currentSectionIndex > 0) {
      const prevSectionIndex = currentSectionIndex - 1;
      const prevSection = sections[prevSectionIndex];

      setCurrentSectionIndex(prevSectionIndex);
      setSectionName(prevSection.name);

      const subResponse = await axios.get(
        `${BASE_URL}/api/subsections/sections-id/${prevSection._id}`,
      );

      if (subResponse.data.success) {
        const subSectionList = subResponse.data.data;
        setSubSections(subSectionList);

        const lastSub = subSectionList[subSectionList.length - 1];
        setCurrentSubSectionIndex(subSectionList.length - 1);
        setSubSectionName(lastSub.name);

        const qResponse = await axios.get(
          `${BASE_URL}/api/questions/subsection/${lastSub._id}`,
        );

        if (qResponse.data.success) {
          const sortedQuestions = qResponse.data.data.sort(
            (a, b) => a.display_order - b.display_order,
          );
          setSectionQuestions(sortedQuestions);
          const prevItems = buildDisplayItems(sortedQuestions);
          setCurrentQuestionIndex(prevItems.length - 1);
          didMove = true;
        }
      }
    }

    if (didMove) {
      setCompletedQuestions((prev) => Math.max(0, prev - 1));
    }

    setSelfRating(0);
    setReflectionAnswer("");
    setSelectedOptionId(null);
  };

  const handleCancelAssessment = async () => {
    setOpenCancelDialog(false);
    setLoading(true);
    await deleteAssessmentData();
  };

  if (loading) {
    return (
      <Box
        sx={{
          textAlign: "center",
          mt: 6,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="body1">
          {" "}
          Please wait while we load your Framework...
        </Typography>
        <CircularProgress size={30} />
      </Box>
    );
  }

  const deleteAssessmentData = async () => {
    cancellingRef.current = true;
    if (assessmentId) {
      try {
        await axios.delete(
          `${BASE_URL}/api/response/${assessmentId}/${peopleId}`,
        );
      } catch (err) {
        console.error("Failed to delete responses:", err);
      }
      try {
        await axios.delete(
          `${BASE_URL}/api/assessments/delete/${assessmentId}`,
        );
      } catch (err) {
        console.error("Failed to delete assessment:", err);
      }
    }
    sessionStorage.removeItem("currentAssessmentId");
    localStorage.removeItem("userData");
    localStorage.removeItem("pendingUser");
    // Logout fires but we don't await — hard redirect happens immediately after
    axios
      .post(`${BASE_URL}/api/admin/logout`, {}, { withCredentials: true })
      .catch(() => {});
    navigate("/sign-in", { replace: true });
  };

  const currentSection = sections[currentSectionIndex];

  return (
    <Box
      className="assessment-container"
      sx={{
        position: "relative",
        px: { xs: "16px", sm: 0 },
        paddingBottom: { xs: "90px", sm: "80px" },
        minHeight: "60vh",
        overflowX: "hidden",
      }}
    >
      <ProgressBar
        progress={progressPercent}
        onCancel={() => setOpenCancelDialog(true)}
      />
      <Typography variant="h6" className="assessment-title">
        Section {currentSection?.display_order ?? currentSectionIndex + 1} :{" "}
        {sectionName.toUpperCase()}
      </Typography>
      <Typography variant="h6" className="assessment-subtitle">
        {currentSection?.display_order ?? currentSectionIndex + 1}.
        {subSections[currentSubSectionIndex]?.display_order ??
          currentSubSectionIndex + 1}{" "}
        {subSectionName}
      </Typography>

      <Box className="question-block" sx={{ mb: 4 }}>
        {!currentDisplayItem ? (
          <Box sx={{ textAlign: "center", mt: 5 }}>
            <Typography variant="h6">No Questions are available</Typography>
          </Box>
        ) : (
          <>
            {/* Objective Question — one per screen */}
            {currentDisplayItem.type === "objective" && (
              <Box className="objective-question-block">
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", gap: "6px" }}
                >
                  <Typography
                    variant="body1"
                    className="objective-question"
                    sx={{ fontWeight: 700, flexShrink: 0 }}
                  >
                    {currentQuestionIndex + 1}.
                  </Typography>
                  <Typography variant="body1" className="objective-question">
                    {currentDisplayItem.data.question_text}
                  </Typography>
                </Box>
                <RadioGroup
                  value={selectedOptionId || ""}
                  onChange={(e) => setSelectedOptionId(e.target.value)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: "8px", sm: "14px" },
                    mt: 1,
                  }}
                >
                  {shuffledOptions.map((option) => (
                    <FormControlLabel
                      key={option._id}
                      value={option._id}
                      control={
                        <Radio
                          size="small"
                          sx={{
                            color: "#90a4ae",
                            alignSelf: "flex-start",
                            fontSize: "15px",
                            padding: 0,
                            paddingRight: "9px",
                            "&.Mui-checked": {
                              color: "#263842",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: { xs: "12px", sm: "15px" },
                            fontFamily: '"Gotham", sans-serif',
                            color: "#263842",
                          }}
                        >
                          {renderOptionText(option.option_text)}
                        </Typography>
                      }
                      className="option-radio-label"
                      sx={{ alignItems: "flex-start" }}
                    />
                  ))}
                </RadioGroup>
              </Box>
            )}

            {/* Reflection Question — always the final screen of a subsection */}
            {currentDisplayItem.type === "reflection" && (
              <Box className="reflection-question-block">
                <Box className="reflection-question">
                  <Typography variant="body1" className="reflection-title">
                    Leader Self-Reflection Question:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "6px",
                    }}
                  >
                    <Typography
                      variant="body1"
                      className="reflection-question"
                      sx={{ fontWeight: 700, flexShrink: 0 }}
                    >
                      {currentQuestionIndex + 1}.
                    </Typography>
                    <Typography variant="body1" className="reflection-question">
                      {currentDisplayItem.data.question_text}
                    </Typography>
                  </Box>
                </Box>

                <Box className="reflection-input-wrapper">
                  <TextField
                    variant="standard"
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={5}
                    placeholder="Type your answer here."
                    className="reflection-textarea"
                    value={reflectionAnswer}
                    onChange={(e) => setReflectionAnswer(e.target.value)}
                    InputProps={{
                      disableUnderline: true,
                    }}
                  />
                  {reflectionAnswer && (
                    <IconButton
                      className="reflection-clear-btn"
                      onClick={() => setReflectionAnswer("")}
                      size="small"
                    >
                      <CloseIcon className="close-icon" fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    sx={{
                      color: "#009ccc",
                      fontSize: { xs: "11px", sm: "14px" },
                      fontWeight: 500,
                    }}
                  >
                    Note : You may want to shortlist the questions that are
                    drawing you to greater dependence on Christ and schedule a
                    separate time of reflection and prayer.
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontSize: { xs: "11px", sm: "14px" } }}
                  >
                    Provide your self rating:
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Box
                        key={star}
                        onClick={() =>
                          setSelfRating((prev) => (prev === star ? 0 : star))
                        }
                        sx={{ cursor: "pointer" }}
                      >
                        {selfRating >= star ? (
                          <StarRateRoundedIcon className="star-filled" />
                        ) : (
                          <StarOutlineRoundedIcon className="star-empty" />
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      <Box
        className="button-block"
        sx={{ flexWrap: "wrap", gap: { xs: 1, sm: "10px" } }}
      >
        <Button
          className="previous-button"
          onClick={handlePrevious}
          sx={{
            visibility:
              currentSectionIndex === 0 &&
              currentSubSectionIndex === 0 &&
              currentQuestionIndex === 0
                ? "hidden"
                : "visible",
          }}
        >
          Previous
        </Button>
        <Button
          className={canProceed ? "next-button" : "next-button-disabled"}
          variant="contained"
          onClick={handleNext}
          disabled={!canProceed || submitting}
          sx={{
            minWidth: "100px",
            position: "relative",
          }}
        >
          {submitting ? (
            <CircularProgress
              size={22}
              color="inherit"
              sx={{
                position: "absolute",
              }}
            />
          ) : (
            <>
              {isLastQuestion ? "Submit" : "Next"}
              <ArrowForwardIcon
                className={canProceed ? "arrow-icon" : "arrow-icon-disabled"}
              />
            </>
          )}
        </Button>
      </Box>
      <SnackbarAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "24px",
            textAlign: "center",
          },
        }}
      >
        <DialogContent>
          {/* Title */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: "15px", sm: "18px" },
              marginBottom: "10px",
            }}
          >
            Are you sure you want to exit?
          </Typography>

          {/* Description */}
          <Typography
            sx={{ fontSize: { xs: "12px", sm: "14px" }, color: "#666" }}
          >
            This will clear all your responses and sign you out. You will be
            redirected to the sign-in page.
          </Typography>

          {/* Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              marginTop: "24px",
            }}
          >
            <Button
              onClick={() => setOpenCancelDialog(false)}
              sx={{
                border: "1px solid #ccc",
                color: "#333",
                padding: { xs: "8px 14px", sm: "6px 18px" },
                borderRadius: "8px",
              }}
            >
              CANCEL
            </Button>

            <Button
              onClick={handleCancelAssessment}
              sx={{
                backgroundColor: "#E53935",
                color: "#fff",
                padding: { xs: "8px 14px", sm: "6px 18px" },
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "#d32f2f",
                },
              }}
            >
              CONFIRM
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={isAssessmentLocked}>
        <DialogContent sx={{ textAlign: "center", py: 4, px: 3 }}>
          <Typography
            variant="h6"
            sx={{ color: "#263842", mb: 2, fontWeight: 600 }}
          >
            Page Under Progress
          </Typography>
          <Typography variant="body1" sx={{ color: "#50545D", mb: 4 }}>
            The page is currently under progress. Please try again after some
            time.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/")}
            className="next-button"
          >
            Go to Home
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Assessment;
